import { useFeatures } from "@/api/app-configuration.api";
import { CheckCard } from "@/components/generic/CheckCard";
import { Checkbox } from "@/components/generic/Checkbox";
import { useIntegrationConnectionInfoQuery } from "@/queries/integrations";
import { Campaign } from "@/types/campaign";
import { MaybePromise } from "@/types/maybe-promise";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, ToOptions } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../../basic/SegmentHeader";

export const contactInformationFormSchema = z.strictObject({
  deliverContacts: z.boolean(),
  deliverContactsRequirePhone: z.boolean(),
  deliverLiveNumber: z.boolean(),
});

export type ContactInformationFormValues = z.infer<
  typeof contactInformationFormSchema
>;

export function ContactInformationForm({
  configurations,
  exportSystem,
  defaultValues,
  values,
  updateValues,
  setSubmitHandler,
  linkToContactsFilter,
  autoSubmit = false,
}: Pick<Campaign, "configurations" | "exportSystem"> & {
  defaultValues: ContactInformationFormValues;
  values?: ContactInformationFormValues;
  updateValues: (values: ContactInformationFormValues) => MaybePromise<void>;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
  linkToContactsFilter: ToOptions;
  autoSubmit?: boolean;
}) {
  const hasContactConfiguration = useMemo(
    () =>
      configurations.some(
        (c) => (c.contactConfiguration?.personas?.length ?? 0) > 0,
      ),
    [configurations],
  );

  const { data: connectionInfo } =
    useIntegrationConnectionInfoQuery(exportSystem);

  const { canSendContactsToIntegration, canSendLiveNumbersToIntegration } =
    useFeatures();

  const {
    reset,
    setValue,
    watch,
    control,
    handleSubmit,
    formState: {
      isDirty,
      isValid,
      isSubmitted,
      isSubmitting,
      defaultValues: formCurrentDefaultValues,
    },
  } = useForm({
    resolver: zodResolver(contactInformationFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues === formCurrentDefaultValues) return;
    reset(defaultValues, { keepValues: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    if (!values) return;
    for (const key of contactInformationFormSchema.keyof().options) {
      setValue(key, values[key]);
    }
  }, [values, setValue]);

  const formData = watch();

  const submit = useMemo(
    () => handleSubmit((values) => updateValues(values)),
    [handleSubmit, updateValues],
  );

  const canSubmit = useMemo(
    () => !isSubmitting && (isValid || !isSubmitted),
    [isSubmitted, isSubmitting, isValid],
  );

  useEffect(
    () =>
      setSubmitHandler?.({
        submit,
        isDirty,
        isPending: isSubmitting,
        isDisabled: !canSubmit,
      }),
    [canSubmit, isDirty, isSubmitting, setSubmitHandler, submit],
  );

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;

    if (autoSubmit) {
      autoSubmittedRef.current = true;
      submit();
    }
  }, [autoSubmit, submit]);

  return (
    <form className="flex flex-col items-center gap-y-10" onSubmit={submit}>
      <div className="flex h-full w-full max-w-2xl flex-col items-center gap-y-16">
        {canSendLiveNumbersToIntegration && (
          <div className="flex flex-col gap-y-10">
            <SegmentHeader
              className="max-w-lg"
              title="Live nummer"
              description={
                <>
                  Vælg om du ønsker at modtage Live numre når der{" "}
                  {exportSystem ? "oprettes" : "modtages"} leads
                  {connectionInfo?.provider
                    ? ` i ${connectionInfo.provider}`
                    : ""}
                  .
                </>
              }
            />

            <div className="flex flex-col gap-4">
              <div className="flex w-lg flex-col items-center">
                <CheckCard
                  control={control}
                  name="deliverLiveNumber"
                  label="Køb live numre"
                />
              </div>
            </div>

            <span className="text-theme-on-background text-center text-xs font-medium">
              Det koster 1 credit per modtaget Live nummer.
            </span>
          </div>
        )}

        {canSendContactsToIntegration && (
          <div className="flex flex-col gap-y-10">
            <SegmentHeader
              className="max-w-lg"
              title="Kontaktpersoner"
              description={
                <>
                  Vælg om du ønsker at{" "}
                  {exportSystem ? "købe og oprette" : "købe"} kontaktpersoner
                  når der {exportSystem ? "oprettes" : "modtages"} leads
                  {connectionInfo?.provider
                    ? ` i ${connectionInfo.provider}`
                    : ""}
                  . Du vælger hvilken kontaktperson der skal{" "}
                  {exportSystem ? "oprettes" : "modtages"}, ved at tilføje
                  filteret "Kontaktpersoner" i din målgruppe.
                </>
              }
            />

            <div className={"flex w-full max-w-lg flex-col gap-4"}>
              <CheckCard
                control={control}
                name="deliverContacts"
                label={`${exportSystem ? "Køb og opret" : "Køb"} kontaktpersoner`}
                isDisabled={!hasContactConfiguration}
              />

              {formData.deliverContacts && (
                <div className="mx-3 overflow-hidden">
                  <Controller
                    control={control}
                    name="deliverContactsRequirePhone"
                    render={({ field }) => (
                      <Checkbox
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        isSelected={field.value}
                        aria-label={`${exportSystem ? "Opret" : "Modtag"} kun kontaktpersoner med direkte telefonnumre`}
                        isDisabled={!hasContactConfiguration}
                      >
                        {exportSystem ? "Opret" : "Modtag"} kun kontaktpersoner
                        med direkte telefonnumre
                      </Checkbox>
                    )}
                  />
                </div>
              )}
            </div>

            <span className="text-theme-on-background text-center text-xs font-medium">
              {!hasContactConfiguration ? (
                <>
                  Du skal{" "}
                  <Link className="text-theme-active" {...linkToContactsFilter}>
                    tilføje kontaktpersoner til din målgruppe
                  </Link>{" "}
                  før at de kan{" "}
                  {exportSystem
                    ? `oprettes i ${connectionInfo?.provider || "din integration"}`
                    : "modtages"}
                  .
                </>
              ) : (
                `Det koster 2 credits per ${exportSystem ? "oprettet" : "modtaget"} kontaktperson`
              )}
            </span>
          </div>
        )}
      </div>
    </form>
  );
}
